import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Package, Upload, Plus, MapPin } from 'lucide-react';
import DateSelector from './analytics/DateSelector';
import DeliveryList from './analytics/DeliveryList';
import FinancialSummary from './analytics/FinancialSummary';
import StatisticsCards from './analytics/StatisticsCards';
import ActionButtons from './analytics/ActionButtons';
import EditDeliveryModal from './analytics/EditDeliveryModal';
import ImageDepositCarouselModal from './analytics/ImageDepositCarouselModal';

function Analytics({ userId, providerId, setActiveComponent }) {
  const [selectedDate, setSelectedDate] = useState(getTodayISO());
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesProvince, setDeliveriesProvince] = useState([]);
  const [orders, setOrders] = useState([]);
  const [paymentImages, setPaymentImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [hasActivePickup, setHasActivePickup] = useState(false);
  const [checkingPickup, setCheckingPickup] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showDeliveryTypeModal, setShowDeliveryTypeModal] = useState(false);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  function getTodayISO() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Check for active pickup orders
  useEffect(() => {
    async function checkActivePickup() {
      setCheckingPickup(true);
      try {
        const response = await axios.get(`${baseUrl}/api/check_active_pickup`, {
          params: { user_id: userId }
        });
        setHasActivePickup(response.data.has_active_pickup || false);
      } catch (error) {
        setHasActivePickup(false);
      } finally {
        setCheckingPickup(false);
      }
    }

    if (userId) {
      checkActivePickup();
    } else {
      setCheckingPickup(false);
    }
  }, [userId, baseUrl]);

  useEffect(() => {
    if (selectedDate) {
      fetchDeliveriesForDate(selectedDate);
    }
  }, [selectedDate, userId]);

  async function fetchDeliveriesForDate(date) {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/api/deliveries_by_date`, {
        params: {
          user_id: userId,
          date: date,
        },
      });
      
      setDeliveries(response.data.deliveries || []);
      setDeliveriesProvince(response.data.deliveries_provincia || []);
      setOrders(response.data.orders || []);
      setPaymentImages(response.data.payment_images || []);
    } catch (error) {
      console.error('Error fetching deliveries:', error);
    } finally {
      setLoading(false);
    }
  }

  function canEdit(delivery) {
    // Only allow editing deliveries with 'pending' status
    if (delivery.status !== 'pending') {
      console.log(`Result: Cannot edit - status is '${delivery.status}' (only 'pending' can be edited)`);
      return false;
    }
    
    // Extract just YYYY-MM-DD from the delivery date string
    let deliveryDateOnly = delivery.delivery_date;
    
    if (deliveryDateOnly.includes('T')) {
      deliveryDateOnly = deliveryDateOnly.split('T')[0];
    } else if (deliveryDateOnly.includes(' ')) {
      deliveryDateOnly = deliveryDateOnly.split(' ')[0];
    }
    
    // Get current date in Peru timezone
    const now = new Date();
    const peruDateFormatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Lima',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    const peruHourFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Lima',
      hour: 'numeric',
      hour12: false
    });
    
    const todayPeru = peruDateFormatter.format(now);
    const currentHourPeru = parseInt(peruHourFormatter.format(now), 10);
    
    
    // If delivery is in the past - cannot edit
    if (deliveryDateOnly < todayPeru) {
      console.log('Result: PAST - cannot edit');
      return false;
    }
    
    // If delivery is for TODAY - can edit ONLY before noon (12 PM)
    if (deliveryDateOnly === todayPeru) {
      const canEditToday = currentHourPeru < 12;
      console.log(`Result: TODAY - hour ${currentHourPeru} < 12? ${canEditToday}`);
      return canEditToday;
    }
    
    // If delivery is in the FUTURE (tomorrow or later) - can edit
    if (deliveryDateOnly > todayPeru) {
      console.log('Result: FUTURE - can edit');
      return true;
    }
    
    return false;
  }

  function calculateFinancials() {
    const isProvider = providerId !== null;
    let totalCollected = 0;
    let totalExchange = 0;
    let totalServiceFee = 0;
    let deliveryCount = 0;
    let regularPriceDeliveries = 0;

    deliveries.forEach(delivery => {
      if (delivery.status === 'entregado' || delivery.status === 'rechazado') {
        deliveryCount++;
        
        // Add charged amount
        if (delivery.charged) {
          totalCollected += parseFloat(delivery.charged) || 0;
        }
        
        // Add fastmax transfer amount
        if (delivery.fastmax_transfer_amount) {
          totalCollected += parseFloat(delivery.fastmax_transfer_amount) || 0;
        }
        
        // Subtract exchange amount
        if (delivery.fastmax_exchange_amount) {
          totalExchange += parseFloat(delivery.fastmax_exchange_amount) || 0;
        }
        
        // Calculate service fee for this delivery
        if (delivery.quince_soles) {
          totalServiceFee += 15;
        } else if (delivery.veinte_soles) {
          totalServiceFee += 20;
        } else {
          // Regular pricing - count these to apply tiered pricing later
          if (isProvider) {
            totalServiceFee += 7.90;
          } else {
            regularPriceDeliveries++;
          }
        }
      }
    });

    // For non-provider users, apply tiered pricing to regular deliveries
    if (!isProvider && regularPriceDeliveries > 0) {
      if (regularPriceDeliveries === 1 && deliveryCount === 1) {
        // Only one delivery total and it's regular price
        totalServiceFee += 15;
      } else {
        // Multiple deliveries or mixed with special pricing
        totalServiceFee += regularPriceDeliveries * 9.90;
      }
    }

    // Subtract exchange from total collected
    totalCollected = totalCollected - totalExchange;
    
    // Calculate total to deposit
    const totalToDeposit = totalCollected - totalServiceFee;

    // Calculate average price per delivery for display
    const pricePerDelivery = deliveryCount > 0 ? totalServiceFee / deliveryCount : 0;

    return {
      totalCollected,
      totalToDeposit,
      deliveryCount,
      pricePerDelivery,
      totalServiceFee,
      totalExchange,
    };
  }

  async function handleUpdateDelivery(deliveryId, updatedData) {
    try {
      const response = await axios.put(
        `${baseUrl}/api/delivery_points/${deliveryId}`,
        {
          user_id: userId,
          ...updatedData,
        }
      );
      
      // Refresh deliveries
      await fetchDeliveriesForDate(selectedDate);
      setEditingDelivery(null);
      
      return response.data;
    } catch (error) {
      console.error('Error updating delivery:', error);
      throw error;
    }
  }

  async function handleDeleteDelivery(deliveryId) {
    console.log('Deleting delivery:', deliveryId);
    
    try {
      const response = await axios.delete(
        `${baseUrl}/api/delivery_points/${deliveryId}`
      );
      
      console.log('DELETE SUCCESS:', response.data);
      
      await fetchDeliveriesForDate(selectedDate);
      setEditingDelivery(null);
      
    } catch (error) {
      console.error('DELETE ERROR:', error.response?.data || error.message);
      throw error;
    }
  }

  function handleEntregaClick() {
    if (!hasActivePickup) {
      return;
    }
    // Show modal instead of directly setting component
    setShowDeliveryTypeModal(true);
  }
  function handleSelectLima() {
    setShowDeliveryTypeModal(false);
    setActiveComponent('Entrega');
  }

  function handleSelectProvincia() {
    setShowDeliveryTypeModal(false);
    setActiveComponent('EntregaProvincia');
  }

  const financials = calculateFinancials();

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setActiveComponent('Options')}
              className="flex items-center gap-2 text-slate-700 hover:text-[#B1336E] transition-colors duration-200 font-montserrat"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm sm:text-base">Volver</span>
            </button>
            
            <h1 className="text-lg sm:text-xl font-extrabold text-[#B1336E] font-montserrat">
              Analítica
            </h1>

            <div className="flex items-center gap-2">
              {/* Entrega Button - NOW OPENS MODAL */}
              <button
                onClick={handleEntregaClick}
                disabled={!hasActivePickup || checkingPickup}
                className={`p-2 rounded-lg transition-colors group relative ${
                  hasActivePickup && !checkingPickup
                    ? 'text-[#B1336E] hover:bg-[#B1336E]/10 cursor-pointer'
                    : 'text-slate-400 bg-slate-100 cursor-not-allowed opacity-50'
                }`}
              >
                <Plus className="w-5 h-5" />
                <span className="absolute top-full right-0 mt-2 px-3 py-2 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none font-montserrat z-50 shadow-lg">
                  {checkingPickup 
                    ? 'Verificando...' 
                    : hasActivePickup 
                      ? 'Agregar Entrega' 
                      : 'Primero agrega un recojo'}
                </span>
              </button>

              {/* Recojo Button */}
              <button
                onClick={() => setActiveComponent('Recojo')}
                className="p-2 text-[#E6B43C] hover:bg-[#E6B43C]/10 rounded-lg transition-colors group relative"
              >
                <MapPin className="w-5 h-5" />
                <span className="absolute top-full right-0 mt-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none font-montserrat z-50">
                  Agregar Recojo
                </span>
              </button>

              {/* Masivo Button */}
              <button
                onClick={() => setActiveComponent('Masivo')}
                className="p-2 text-[#67A43C] hover:bg-[#67A43C]/10 rounded-lg transition-colors group relative"
              >
                <Upload className="w-5 h-5" />
                <span className="absolute top-full right-0 mt-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none font-montserrat z-50">
                  Carga Masiva
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
          
          {/* Date Selector */}
          <DateSelector
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            showCalendar={showCalendar}
            onToggleCalendar={() => setShowCalendar(!showCalendar)}
          />

          {/* Statistics Cards */}
          <StatisticsCards deliveries={deliveries} orders={orders} />

          {/* Financial Summary */}
          <FinancialSummary 
            financials={financials}
            paymentImages={paymentImages}
            onShowImages={() => setShowImageModal(true)}
          />

          {/* Action Buttons or Delivery List */}
          {deliveries.length === 0 && deliveriesProvince.length === 0 && orders.length === 0 ? (
            <ActionButtons 
              selectedDate={selectedDate} 
              setActiveComponent={setActiveComponent} 
              hasActivePickup={hasActivePickup}
            />
          ) : (
            <DeliveryList
              deliveries={deliveries}
              deliveriesProvince={deliveriesProvince}
              orders={orders}
              canEdit={canEdit}
              onEdit={setEditingDelivery}
              loading={loading}
              selectedDate={selectedDate}
              setActiveComponent={setActiveComponent}
            />
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingDelivery && (
        <EditDeliveryModal
          delivery={editingDelivery}
          onClose={() => setEditingDelivery(null)}
          onSave={handleUpdateDelivery}
          onDelete={handleDeleteDelivery}
        />
      )}

      {/* Image Carousel Modal */}
      {showImageModal && (
        <ImageDepositCarouselModal
        images={paymentImages}
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
      />
      )}
      {showDeliveryTypeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-2xl">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-slate-900 font-montserrat mb-2">
                Selecciona el Tipo de Entrega
              </h3>
              <p className="text-sm text-slate-600 font-montserrat">
                ¿Dónde se realizará la entrega?
              </p>
            </div>

            <div className="space-y-3">
              {/* Lima Option */}
              <button
                onClick={handleSelectLima}
                className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white py-4 px-6 rounded-lg transition-all font-montserrat font-semibold flex items-center justify-between group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className="text-base">Lima Metropolitana</div>
                    <div className="text-xs text-white/80 font-normal">Entrega local con motorizado</div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>

              {/* Provincia Option */}
              <button
                onClick={handleSelectProvincia}
                className="w-full bg-brand-gold hover:bg-brand-gold/90 text-white py-4 px-6 rounded-lg transition-all font-montserrat font-semibold flex items-center justify-between group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Package className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className="text-base">Provincia</div>
                    <div className="text-xs text-white/80 font-normal">Envío por agencia de transporte</div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>

            <button
              onClick={() => setShowDeliveryTypeModal(false)}
              className="w-full mt-4 bg-slate-200 hover:bg-slate-300 text-slate-700 py-2 rounded-lg transition-colors font-montserrat font-medium"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
      
    </div>
  );
}

export default Analytics;